// Create / Edit Custom Lens modal (Figma: Create & Edit Custom Lens).

import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Spin, Tooltip } from 'antd';
import { CloseCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import styled from '@emotion/styled';

import { colorConstants } from 'config/colorConstants';
import useFetchLensConfig from 'hooks/ppa/useFetchLensConfig';
import useUpdateLens from 'hooks/ppa/useUpdateLens';
import useUpsertCustomLens from 'hooks/ppa/useUpsertCustomLens';
import useDeleteCustomLens from 'hooks/ppa/useDeleteCustomLens';

import { LensEditWarningBanner, LensModalStyled } from './customLens.styled';
import DeleteCustomLensModal from './DeleteCustomLensModal';
import SelectedItemsPanel from './SelectedItemsPanel';
import SelectionCountDropdown from './SelectionCountDropdown';
import type { LensConfigApiResponse } from './customLens.api.types';
import { DISABLED_ITEMS_MESSAGES } from './customLens.constants';
import {
    applyLensDataToForm,
    buildCreateCustomLensRequest,
    buildCustomLensUpdateRequest,
    buildDeleteCustomLensRequest,
    filterConflictingSelections,
    getCustomLensFormValidation,
    getVisibleSelectionFields,
    parseCustomLensEditFormFromApi,
    EMPTY_CUSTOM_LENS_PARSED_CONFIG,
    parseCustomLensConfigFromApi,
    removeFromCategorySelections,
    resetFormState,
    syncOrderedSelection,
} from './customLens.helpers';
import {
    CUSTOM_LENS_TOOLTIP_PROPS,
    CUSTOM_LENS_UI,
    CUSTOM_LENS_WARNING_BANNER_STYLE,
} from './customLens.ui';
import type { CreateCustomLensModalProps, CustomLensFormData } from './customLens.types';

// ─── Styled components ───────────────────────────────────────────────────────

const DeleteLensButton = styled(Button)`
    background-color: ${colorConstants.redAlt} !important;
    border-color: ${colorConstants.redAlt} !important;
    color: ${colorConstants.white} !important;
    border-radius: ${CUSTOM_LENS_UI.BUTTON_BORDER_RADIUS}px !important;
    box-shadow: none !important;
    font-weight: 400;
`;

const CancelLensButton = styled(Button)`
    color: ${colorConstants.blackTransparent88} !important;
    font-weight: 600 !important;
    border-radius: ${CUSTOM_LENS_UI.BUTTON_BORDER_RADIUS}px !important;
    box-shadow: none !important;
`;

const SaveLensButton = styled(Button)`
    font-weight: 600 !important;
    border-radius: ${CUSTOM_LENS_UI.BUTTON_BORDER_RADIUS}px !important;
    box-shadow: none !important;
`;

// ─── Component ───────────────────────────────────────────────────────────────

const CreateCustomLensModal: FC<CreateCustomLensModalProps> = ({
    open = false,
    isEdit = false,
    lensData,
    existingLensNames = [],
    scenarioId = null,
    baseViewId = null,
    onCancel = () => {},
    onOk = () => {},
    onDelete = () => {},
}) => {
    const fetchLensId = isEdit && lensData?.id != null ? Number(lensData.id) : null;
    const {
        data: lensConfigData,
        isLoading: isLensConfigLoading,
        isPending: isLensConfigPending,
    } = useFetchLensConfig(fetchLensId, open);

    const apiViewConfig = (lensConfigData?.raw as LensConfigApiResponse | undefined)?.view_config;

    const { loading: isSaving, mutate: upsertCustomLens } = useUpsertCustomLens();
    const { loading: isUpdating, mutate: updateLens } = useUpdateLens();
    const { loading: isDeleting, mutate: deleteCustomLens } = useDeleteCustomLens();
    const apiParsedConfig = useMemo(
        () => parseCustomLensConfigFromApi(lensConfigData?.raw),
        [lensConfigData?.raw],
    );
    const parsedConfig = apiParsedConfig ?? EMPTY_CUSTOM_LENS_PARSED_CONFIG;
    const {
        featureName,
        lensNameMaxLength,
        lensNamePlaceholder,
        showIncludeAiSuggestions,
        includeAiSuggestionsLabel,
        includeAiSuggestionsDisabled,
        dimensionSectionLabel,
        kpiSectionLabel,
        selectedDimensionsPanelTitle,
        selectedKpisPanelTitle,
        dimensionFields,
        kpiFields,
    } = parsedConfig;

    const [lensName, setLensName] = useState('');
    const [lensNameTouched, setLensNameTouched] = useState(false);
    const [includeAiSuggestions, setIncludeAiSuggestions] = useState(parsedConfig.includeAiSuggestionsDefault);
    const [dimensionSelections, setDimensionSelections] = useState(parsedConfig.initialDimensionSelections);
    const [kpiSelections, setKpiSelections] = useState(parsedConfig.initialKpiSelections);
    const [selectedDimensionOrder, setSelectedDimensionOrder] = useState<string[]>([]);
    const [selectedKpiOrder, setSelectedKpiOrder] = useState<string[]>([]);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [lensId, setLensId] = useState<string | number | undefined>();
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);

    const visibleDimensionFields = useMemo(
        () => getVisibleSelectionFields(dimensionFields, isEdit, dimensionSelections),
        [dimensionFields, isEdit, dimensionSelections],
    );
    const visibleKpiFields = useMemo(
        () => getVisibleSelectionFields(kpiFields, isEdit, kpiSelections),
        [kpiFields, isEdit, kpiSelections],
    );
    const apiFormData = useMemo(() => {
        if (!isEdit || !lensConfigData?.raw) {
            return null;
        }

        const hasFieldConfig =
            parsedConfig.dimensionFields.length > 0 || parsedConfig.kpiFields.length > 0;
        if (!hasFieldConfig) {
            return null;
        }

        return parseCustomLensEditFormFromApi(lensConfigData.raw, parsedConfig);
    }, [isEdit, lensConfigData?.raw, parsedConfig]);

    useEffect(() => {
        if (!open) {
            setIsDeleteConfirmOpen(false);
            return;
        }

        setLensNameTouched(false);

        if (!apiParsedConfig) {
            return;
        }

        if (isEdit) {
            if (!apiFormData) {
                return;
            }

            const form = applyLensDataToForm(apiFormData, parsedConfig);
            setLensName(form.lensName);
            setIncludeAiSuggestions(form.includeAiSuggestions);
            setDimensionSelections(form.dimensionSelections);
            setKpiSelections(form.kpiSelections);
            setSelectedDimensionOrder(form.selectedDimensionOrder);
            setSelectedKpiOrder(form.selectedKpiOrder);
            setUpdatedAt(form.updatedAt ?? null);
            setLensId(apiFormData.id);
            return;
        }

        const form = resetFormState(parsedConfig);
        setLensName(form.lensName);
        setIncludeAiSuggestions(form.includeAiSuggestions);
        setDimensionSelections(form.dimensionSelections);
        setKpiSelections(form.kpiSelections);
        setSelectedDimensionOrder(form.selectedDimensionOrder);
        setSelectedKpiOrder(form.selectedKpiOrder);
        setUpdatedAt(null);
        setLensId(undefined);
    }, [open, isEdit, apiFormData, apiParsedConfig, parsedConfig]);

    const originalLensName = isEdit ? apiFormData?.name ?? lensData?.name : undefined;
    const {
        lensNameError,
        hasSelections,
        canSave,
        showLensNameError,
        showLensNameBlockingError,
        createButtonTooltip,
    } = getCustomLensFormValidation({
        isEdit,
        lensName,
        lensNameTouched,
        lensNameMaxLength,
        existingLensNames,
        originalLensName,
        selectedDimensionOrder,
        selectedKpiOrder,
    });

    const isSavingLens = isSaving || isUpdating;
    const defaultModalTitle = isEdit ? 'Edit Custom Lens' : 'Create Custom Lens';
    const isInitialLoading =
        open &&
        (isLensConfigLoading ||
            isLensConfigPending ||
            (isEdit ? !apiFormData : !apiParsedConfig));
    const isModalBusy = isInitialLoading || isSavingLens;

    const modalTitle =
        apiViewConfig?.title ?? (isEdit ? 'Edit Custom Lens' : featureName || 'Create Custom Lens');
    const saveButtonLabel = isEdit
        ? apiViewConfig?.actions?.primary?.label || 'Save'
        : apiViewConfig?.actions?.primary?.label || 'Create';
    const cancelButtonLabel = apiViewConfig?.actions?.secondary?.label || 'Cancel';

    // ─── Handlers ──────────────────────────────────────────────────────────

    const handleDimensionChange = (fieldKey: string, selected: string[]) => {
        const filtered = filterConflictingSelections(selected, selectedKpiOrder);
        setDimensionSelections((prev) => {
            const next = { ...prev, [fieldKey]: filtered };
            setSelectedDimensionOrder((order) => syncOrderedSelection(order, next));
            return next;
        });
    };

    const handleKpiChange = (fieldKey: string, selected: string[]) => {
        const filtered = filterConflictingSelections(selected, selectedDimensionOrder);
        setKpiSelections((prev) => {
            const next = { ...prev, [fieldKey]: filtered };
            setSelectedKpiOrder((order) => syncOrderedSelection(order, next));
            return next;
        });
    };

    const handleRemoveDimension = (item: string) => {
        setSelectedDimensionOrder((order) => order.filter((name) => name !== item));
        setDimensionSelections((prev) => removeFromCategorySelections(prev, item));
    };

    const handleRemoveKpi = (item: string) => {
        setSelectedKpiOrder((order) => order.filter((name) => name !== item));
        setKpiSelections((prev) => removeFromCategorySelections(prev, item));
    };

    const buildFormData = (): CustomLensFormData => ({
        id: lensId,
        name: lensName.trim(),
        updatedAt,
        includeAiSuggestions,
        dimensionSelections,
        kpiSelections,
        selectedDimensionOrder,
        selectedKpiOrder,
    });

    const handleSave = () => {
        if (!canSave || isModalBusy) return;

        const formData = buildFormData();

        if (isEdit && lensId != null) {
            updateLens(
                {
                    lensId: Number(lensId),
                    payload: {
                        ...buildCustomLensUpdateRequest(formData, parsedConfig),
                        ...(baseViewId != null ? { origin_view_id: baseViewId } : {}),
                        ...(scenarioId != null ? { origin_scenario_id: scenarioId } : {}),
                    },
                },
                {
                    onSuccess: (response) => {
                        onOk(formData, response);
                        onCancel();
                    },
                },
            );
            return;
        }

        const lensRequest = {
            ...buildCreateCustomLensRequest(formData, parsedConfig),
            ...(scenarioId != null
                ? {
                      scenario_id: scenarioId,
                      ...(baseViewId != null ? { base_view_id: baseViewId } : {}),
                  }
                : {}),
        };

        upsertCustomLens(lensRequest, {
            onSuccess: (response) => {
                onOk(formData, response);
                onCancel();
            },
        });
    };

    const handleConfirmDelete = () => {
        if (lensId == null || lensId === '' || isDeleting) return;

        const deleteRequest = buildDeleteCustomLensRequest(lensId);

        deleteCustomLens(deleteRequest, {
            onSuccess: (response) => {
                setIsDeleteConfirmOpen(false);
                onDelete(
                    lensId,
                    response.fallback_applied && response.fallback_lens_id != null
                        ? {
                              fallbackLensId: response.fallback_lens_id,
                              fallbackLensName: response.fallback_lens_name ?? undefined,
                          }
                        : undefined,
                );
                onCancel();
            },
        });
    };

    const handleLensNameChange = (value: string) => {
        setLensNameTouched(true);
        setLensName(value);
    };

    const handleClearLensName = () => {
        setLensNameTouched(true);
        setLensName('');
    };

    const saveButton = (
        <SaveLensButton
            type="primary"
            disabled={!canSave || isModalBusy}
            loading={isSavingLens}
            onClick={handleSave}
        >
            {saveButtonLabel}
        </SaveLensButton>
    );

    const handleModalCancel = () => {
        if (isModalBusy) {
            return;
        }

        onCancel();
    };

    const modalHeader = (
        <div className="flex flex-col gap-3 pr-8">
            <span className="text-[16px] font-semibold leading-6">{modalTitle}</span>
            <div className="font-normal" style={{ width: CUSTOM_LENS_UI.LENS_NAME_INPUT_WIDTH }}>
                <div className="relative">
                    <Input
                        value={lensName}
                        maxLength={lensNameMaxLength}
                        placeholder={lensNamePlaceholder}
                        disabled={isModalBusy}
                        className={`!rounded-md !pr-[88px] ${showLensNameBlockingError ? '!border-redAlt' : ''}`}
                        status={showLensNameBlockingError ? 'error' : undefined}
                        onChange={(e) => handleLensNameChange(e.target.value)}
                    />
                    <div className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
                        <span className="text-xs font-normal text-fontGreyAlt1">
                            {lensName.length} / {lensNameMaxLength}
                        </span>
                        {lensName.length > 0 && (
                            <button
                                type="button"
                                aria-label="Clear lens name"
                                disabled={isModalBusy}
                                className="pointer-events-auto flex items-center justify-center border-0 bg-transparent p-0 text-fontGreyAlt1 hover:text-fontGreyAlt disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={handleClearLensName}
                            >
                                <CloseCircleFilled className="text-sm" />
                            </button>
                        )}
                    </div>
                </div>
                {showLensNameError && (
                    <p className="m-0 mt-1 text-xs leading-5 text-redAlt">{lensNameError}</p>
                )}
            </div>
        </div>
    );

    const modalFooter = (
        <div className="flex w-full items-center justify-between">
            {isEdit ? (
                <DeleteLensButton disabled={isModalBusy} onClick={() => setIsDeleteConfirmOpen(true)}>
                    Delete
                </DeleteLensButton>
            ) : (
                <span />
            )}
            <div className="flex gap-2">
                <CancelLensButton disabled={isModalBusy} onClick={handleModalCancel}>
                    {cancelButtonLabel}
                </CancelLensButton>
                {createButtonTooltip ? (
                    <Tooltip {...CUSTOM_LENS_TOOLTIP_PROPS} title={createButtonTooltip}>
                        <span className="inline-flex">{saveButton}</span>
                    </Tooltip>
                ) : (
                    saveButton
                )}
            </div>
        </div>
    );

    // ─── Render ────────────────────────────────────────────────────────────

    return (
        <>
            <LensModalStyled
                open={open}
                title={
                    isModalBusy ? (
                        <span className="text-[16px] font-semibold leading-6">{defaultModalTitle}</span>
                    ) : (
                        modalHeader
                    )
                }
                footer={modalFooter}
                width={1040}
                centered
                zIndex={CUSTOM_LENS_UI.LENS_MODAL_Z_INDEX}
                maskClosable={false}
                closable={!isModalBusy}
                destroyOnClose
                onCancel={handleModalCancel}
                bodyStyle={{ padding: '0px', height: '500px' }}
                style={{ borderRadius: CUSTOM_LENS_UI.MODAL_BORDER_RADIUS }}
            >
                {isModalBusy ? (
                    <div className="flex h-full min-h-[500px] items-center justify-center border border-borderGreyAlt bg-white">
                        <Spin size="default" />
                    </div>
                ) : (
                <div className="flex h-full flex-col gap-2">
                    {isEdit && (
                        <LensEditWarningBanner>
                            <ExclamationCircleFilled
                                className="mt-0.5 shrink-0 text-base"
                                style={{ color: CUSTOM_LENS_WARNING_BANNER_STYLE.iconColor }}
                            />
                            <span>
                                Any change to this lens will impact all scenarios and views where it is being used.
                            </span>
                        </LensEditWarningBanner>
                    )}

                    <div className="flex h-full flex-1 overflow-hidden border border-borderGreyAlt bg-white">
                            {/* Left panel — field selectors */}
                            <div className="flex w-[386px] shrink-0 flex-col gap-4 overflow-y-auto border-r border-borderGreyAlt p-4">
                                {showIncludeAiSuggestions ? (
                                    <Checkbox
                                        checked={includeAiSuggestions}
                                        disabled={includeAiSuggestionsDisabled}
                                        onChange={(e) => setIncludeAiSuggestions(e.target.checked)}
                                    >
                                        {includeAiSuggestionsLabel}
                                    </Checkbox>
                                ) : null}

                                {visibleDimensionFields.length > 0 ? (
                                    <div>
                                        <p className="mb-3 text-smallWord font-normal text-fontGreyAlt">
                                            {dimensionSectionLabel}:
                                        </p>
                                        <div className="flex flex-col gap-2">
                                            {visibleDimensionFields.map((field) => (
                                                <SelectionCountDropdown
                                                    key={`dimension-${field.key}`}
                                                    field={field}
                                                    selectedOptions={dimensionSelections[field.key] ?? []}
                                                    disabledOptions={selectedKpiOrder}
                                                    disabledItemsMessage={DISABLED_ITEMS_MESSAGES.UNDER_KPI}
                                                    onChange={(selected) => handleDimensionChange(field.key, selected)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : null}

                                {visibleKpiFields.length > 0 ? (
                                    <div>
                                        <p className="mb-3 text-smallWord font-normal text-fontGreyAlt">
                                            {kpiSectionLabel}:
                                        </p>
                                        <div className="flex flex-col gap-2">
                                            {visibleKpiFields.map((field) => (
                                                <SelectionCountDropdown
                                                    key={field.key}
                                                    field={field}
                                                    selectedOptions={kpiSelections[field.key] ?? []}
                                                    disabledOptions={selectedDimensionOrder}
                                                    disabledItemsMessage={DISABLED_ITEMS_MESSAGES.UNDER_DIMENSION}
                                                    onChange={(selected) => handleKpiChange(field.key, selected)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            {/* Right panel — selected items or empty state */}
                            {hasSelections ? (
                                <>
                                    <SelectedItemsPanel
                                        title={selectedDimensionsPanelTitle}
                                        emptyText="No dimensions selected yet"
                                        items={selectedDimensionOrder}
                                        onReorder={setSelectedDimensionOrder}
                                        onRemove={handleRemoveDimension}
                                        className="relative flex-1 after:absolute after:bottom-3 after:right-0 after:top-3 after:w-px after:bg-borderGreyAlt after:content-['']"
                                    />
                                    <SelectedItemsPanel
                                        title={selectedKpisPanelTitle}
                                        emptyText="No KPIs selected yet"
                                        items={selectedKpiOrder}
                                        onReorder={setSelectedKpiOrder}
                                        onRemove={handleRemoveKpi}
                                        className="flex-1"
                                    />
                                </>
                            ) : (
                                <div className="flex min-h-0 flex-1 flex-col bg-[#fcfcfd]">
                                <div className="flex flex-1 flex-col items-center justify-center gap-1 px-4 text-center">
                                    <p className="m-0 text-[16px] font-medium leading-6 text-blackAlt">
                                        No Dimensions or KPIs Added yet
                                    </p>
                                    <p className="m-0 text-[14px] font-normal leading-[22px] text-fontGreyAlt1">
                                        Add KPIs to custom lens to rearrange them here
                                    </p>
                                </div>
                                </div>
                            )}
                        </div>
                </div>
                )}
            </LensModalStyled>

            <DeleteCustomLensModal
                open={isDeleteConfirmOpen}
                lensName={lensName.trim()}
                confirmLoading={isDeleting}
                onCancel={() => setIsDeleteConfirmOpen(false)}
                onDelete={handleConfirmDelete}
            />
        </>
    );
};

export default CreateCustomLensModal;
